import { buildTracePropagationTargets } from "@fluentry/domain";
import { API_BASE_URL } from "./api";
import { clientEnv } from "./client-env";

let sentryInitialized = false;
let sentryClientPromise: Promise<typeof import("@sentry/react")> | null = null;

function loadSentryClient() {
  // Admin is often opened for fast operational checks; load Sentry only after DSN gating confirms observability is active.
  sentryClientPromise ??= import("@sentry/react");
  return sentryClientPromise;
}

function parseSampleRate(raw: string | undefined, fallback: number) {
  const parsed = Number.parseFloat(raw ?? "");
  return Number.isFinite(parsed) ? parsed : fallback;
}

function resolveRelease() {
  return clientEnv.VITE_SENTRY_RELEASE;
}

function runtimeTags() {
  return {
    runtime_channel: "web",
    runtime_platform: "browser",
    surface: "admin",
    surface_kind: "browser",
  } as const;
}

export function initAdminSentry() {
  if (sentryInitialized) {
    return;
  }

  const dsn = clientEnv.VITE_SENTRY_DSN;
  if (typeof dsn !== "string" || dsn.length === 0) {
    return;
  }

  void loadSentryClient().then((sentry) => {
    if (sentryInitialized) {
      return;
    }

    sentry.init({
      dsn,
      environment: clientEnv.VITE_APP_ENV ?? (clientEnv.DEV ? "dev" : "prod"),
      release: resolveRelease(),
      integrations: [sentry.browserTracingIntegration(), sentry.replayIntegration()],
      tracePropagationTargets: buildTracePropagationTargets(API_BASE_URL),
      tracesSampleRate: parseSampleRate(clientEnv.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.1),
      replaysSessionSampleRate: parseSampleRate(
        clientEnv.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE,
        0.02,
      ),
      replaysOnErrorSampleRate: parseSampleRate(
        clientEnv.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
        1,
      ),
      initialScope: {
        tags: runtimeTags(),
      },
    });

    sentryInitialized = true;
  });
}

export function setAdminUserContext(context: {
  adminRole?: string | null;
  authMode: "better-auth-local" | "cloudflare-access";
  email?: string | null;
  route?: string | null;
  userId?: string | null;
}) {
  if (!sentryInitialized) {
    return;
  }

  void loadSentryClient().then((sentry) => {
    // Keep operator identity and access mode on the shared scope so admin issues can be filtered by route and duty role.
    sentry.setUser(
      context.userId
        ? {
            id: context.userId,
            email: context.email ?? undefined,
          }
        : null,
    );
    sentry.setTags({
      ...runtimeTags(),
      admin_auth_mode: context.authMode,
      admin_role: context.adminRole ?? "unknown",
      admin_route: context.route ?? "unknown",
    });
  });
}

export async function captureAdminException(
  error: unknown,
  context?: {
    action?: string;
    extras?: Record<string, unknown>;
    feature?: string;
    tags?: Record<string, string | number | boolean | null | undefined>;
  },
) {
  const dsn = clientEnv.VITE_SENTRY_DSN;
  if (typeof dsn !== "string" || dsn.length === 0) {
    return;
  }

  const sentry = await loadSentryClient();
  sentry.withScope((scope) => {
    scope.setTag("feature", context?.feature ?? "admin");
    if (context?.action) {
      scope.setTag("action", context.action);
    }
    for (const [key, value] of Object.entries(context?.tags ?? {})) {
      if (value !== undefined && value !== null) {
        scope.setTag(key, String(value));
      }
    }
    for (const [key, value] of Object.entries(context?.extras ?? {})) {
      scope.setExtra(key, value);
    }
    sentry.captureException(error instanceof Error ? error : new Error(String(error)));
  });
}
